import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Pencil, Play, CheckCircle2, RotateCcw, Trash2, XCircle, CalendarClock, Copy, Link2, ExternalLink, History, MessageSquare, Send, Flag } from "lucide-react";
import { founderChip, priorityBadge, statusBadge, fmtMinutes, RELATION_FIELDS } from "@/data/taskOptions";

const Row = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#5A6B8C]">{label}</p>
    <p className="mt-0.5 whitespace-pre-wrap text-sm text-[#14245F]">{value?.toString().trim() ? value : <span className="text-[#9DAAC6]">Not set</span>}</p>
  </div>
);

export const TaskDetail = ({ task, relations, relatedNames, canWrite, onOpenChange, onEdit, onStart, onComplete, onReopen, onCancel, onDelete, onReschedule, onDuplicate, onComment, onOpenRelated }) => {
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [showReschedule, setShowReschedule] = useState(false);
  const [comment, setComment] = useState("");
  const [mentions, setMentions] = useState([]);
  const [posting, setPosting] = useState(false);
  useEffect(() => { setShowReschedule(false); setRescheduleDate(task?.task_date || ""); setComment(""); setMentions([]); }, [task]);
  if (!task) return null;

  const activeRelations = RELATION_FIELDS.filter((rf) => task[rf.key]);
  const comments = task.comments || [];
  const toggleMention = (who) => setMentions((m) => (m.includes(who) ? m.filter((x) => x !== who) : [...m, who]));

  const postComment = async () => {
    const text = comment.trim();
    if (!text) return;
    setPosting(true);
    try { await onComment(task, text, mentions); setComment(""); setMentions([]); } finally { setPosting(false); }
  };

  return (
    <Dialog open={!!task} onOpenChange={onOpenChange}>
      <DialogContent data-testid="task-detail" className="max-h-[92vh] max-w-2xl overflow-y-auto border-[#E4E9F3] bg-white">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={statusBadge(task.status)} label={task.status} />
            <StatusBadge status={priorityBadge(task.priority)} label={task.priority} />
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${founderChip(task.assigned_to)}`}>{task.assigned_to}</span>
            <span className="rounded-full bg-[#EEF2FB] px-2 py-0.5 text-[10px] font-semibold text-[#5A6B8C]">{task.workstream}</span>
          </div>
          <DialogTitle className="mt-2 font-heading text-xl text-[#14245F]">{task.title}</DialogTitle>
          <DialogDescription className="text-[#5A6B8C]">
            Scheduled {task.task_date || "—"}{task.due_date ? ` · due ${task.due_date}${task.due_time ? ` ${task.due_time}` : ""}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Row label="Description" value={task.description} />
            <Row label="Notes" value={task.notes} />
            <Row label="Estimated time" value={fmtMinutes(task.estimated_minutes)} />
            <Row label="Actual time" value={fmtMinutes(task.actual_minutes)} />
            <Row label="Created by" value={task.created_by} />
            <Row label="Completed" value={task.completed_at ? task.completed_at.slice(0, 16).replace("T", " ") : ""} />
          </div>

          {activeRelations.length > 0 && (
            <div className="rounded-xl border border-[#E4E9F3] bg-[#F7F9FE] p-3" data-testid="task-relations">
              <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#5A6B8C]"><Link2 size={12} /> Linked records</p>
              <div className="flex flex-wrap gap-2">
                {activeRelations.map((rf) => {
                  const name = relatedNames[task[rf.key]];
                  const missing = !name;
                  return (
                    <button
                      key={rf.key}
                      data-testid={`task-related-${rf.key}`}
                      disabled={missing}
                      onClick={() => onOpenRelated(rf, task[rf.key])}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${missing ? "cursor-not-allowed border-[#E4E9F3] text-[#9DAAC6]" : "border-[#1D4ED8]/30 text-[#1D4ED8] hover:bg-[#1D4ED8]/5"}`}
                    >
                      {rf.label}: {missing ? "unavailable" : name} {!missing && <ExternalLink size={11} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {canWrite && (
            <div className="flex flex-wrap gap-2 border-t border-[#EEF2FB] pt-4">
              <Button data-testid="task-detail-edit" onClick={() => onEdit(task)} className="rounded-full bg-[#1D4ED8] text-white hover:bg-[#1A45C0]"><Pencil size={14} /> Edit</Button>
              {task.status === "To do" && <Button data-testid="task-detail-start" onClick={() => onStart(task)} className="rounded-full bg-[#06B6D4] text-white hover:bg-[#0891B2]"><Play size={14} /> Start</Button>}
              {task.status !== "Complete" && task.status !== "Cancelled" && <Button data-testid="task-detail-complete" onClick={() => onComplete(task)} className="rounded-full bg-[#16A34A] text-white hover:bg-[#15803D]"><CheckCircle2 size={14} /> Complete</Button>}
              {task.status === "Complete" && <Button data-testid="task-detail-reopen" variant="outline" onClick={() => onReopen(task)} className="rounded-full border-[#E4E9F3]"><RotateCcw size={14} /> Reopen</Button>}
              <Button data-testid="task-detail-reschedule" variant="outline" onClick={() => setShowReschedule((s) => !s)} className="rounded-full border-[#E4E9F3]"><CalendarClock size={14} /> Reschedule</Button>
              <Button data-testid="task-detail-duplicate" variant="outline" onClick={() => onDuplicate(task, "tomorrow")} className="rounded-full border-[#E4E9F3]"><Copy size={14} /> Duplicate to tomorrow</Button>
              {task.status !== "Cancelled" && <Button data-testid="task-detail-cancel" variant="outline" onClick={() => onCancel(task)} className="rounded-full border-[#E4E9F3] text-[#9DAAC6]"><XCircle size={14} /> Cancel</Button>}
              <Button data-testid="task-detail-delete" variant="outline" onClick={() => onDelete(task)} className="rounded-full border-[#E4E9F3] text-[#DC2626] hover:bg-[#DC2626]/10"><Trash2 size={14} /> Delete</Button>
            </div>
          )}

          {showReschedule && canWrite && (
            <div className="flex items-center gap-2 rounded-xl bg-[#EEF2FB] p-3" data-testid="task-reschedule-row">
              <input type="date" data-testid="task-reschedule-date" value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)} className="rounded-lg border border-[#E4E9F3] bg-white px-3 py-1.5 text-sm" />
              <Button size="sm" data-testid="task-reschedule-confirm" onClick={() => { onReschedule(task, rescheduleDate); setShowReschedule(false); }} className="rounded-full bg-[#1D4ED8] text-white">Move</Button>
            </div>
          )}

          <div className="border-t border-[#EEF2FB] pt-4">
            <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#5A6B8C]"><MessageSquare size={12} /> Notes {comments.length > 0 && <span className="rounded-full bg-[#EEF2FB] px-1.5 text-[#5A6B8C]">{comments.length}</span>}</p>
            {comments.length > 0 && (
              <ul className="mb-3 space-y-2" data-testid="task-comments">
                {comments.map((c) => (
                  <li key={c.id} className="rounded-xl border border-[#E4E9F3] bg-[#F7F9FE] px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-bold text-[#14245F]">{c.author}</span>
                      <span className="text-[10px] text-[#9DAAC6]">{(c.at || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm text-[#5A6B8C]">{c.text}</p>
                    {(c.mentions || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {c.mentions.map((m) => (
                          <span key={m} className="inline-flex items-center gap-1 rounded-full bg-[#E0A82E]/15 px-2 py-0.5 text-[10px] font-bold text-[#B8791A]"><Flag size={9} /> {m}</span>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {canWrite && (
              <div className="space-y-2" data-testid="task-comment-box">
                <div className="flex items-end gap-2">
                  <textarea
                    data-testid="task-comment-input"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) postComment(); }}
                    rows={2}
                    placeholder="Leave a quick note…"
                    className="flex-1 resize-none rounded-xl border border-[#E4E9F3] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30"
                  />
                  <Button size="icon" data-testid="task-comment-submit" disabled={posting || !comment.trim()} onClick={postComment} className="h-9 w-9 shrink-0 rounded-full bg-[#06B6D4] text-white hover:bg-[#0891B2]"><Send size={15} /></Button>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#5A6B8C]">
                  <span className="font-semibold">Flag:</span>
                  {["Steven", "Richard"].map((who) => (
                    <button
                      key={who}
                      type="button"
                      data-testid={`task-mention-${who}`}
                      onClick={() => toggleMention(who)}
                      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-semibold transition-colors ${mentions.includes(who) ? "border-transparent bg-[#E0A82E] text-white" : "border-[#E4E9F3] text-[#5A6B8C] hover:border-[#E0A82E]/50"}`}
                    >
                      <Flag size={11} /> {who}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {comments.length === 0 && !canWrite && <p className="text-sm text-[#9DAAC6]">No notes yet.</p>}
          </div>

          {(task.history?.length > 0) && (
            <div className="border-t border-[#EEF2FB] pt-4">
              <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#5A6B8C]"><History size={12} /> History</p>
              <ul className="space-y-1.5" data-testid="task-history">
                {[...task.history].reverse().slice(0, 12).map((h, i) => (
                  <li key={`${h.at || ""}-${i}`} className="flex items-start justify-between gap-2 text-xs text-[#5A6B8C]">
                    <span><span className="font-semibold text-[#14245F]">{h.actor}</span> · {h.detail || h.type}</span>
                    <span className="shrink-0 text-[#9DAAC6]">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
